import { FC, useState } from "react";
import { isEmpty } from "ramda";
import {RegistrationSectionInfo} from "@mediaspace/shared/types/RegistrationFieldInfo";
import {baseUrl} from "@mediaspace/shared/utils";
import RegistrationErrors from "./RegistrationErrors";
import {QueryParams} from "@mediaspace/shared/types";
import RegistrationForm, {UserData} from "./RegistrationForm";
import {kmsConnect, WrappedProps as KmsConnectProps} from "@mediaspace/shared/kms-connect";
import { RegistrationType } from "./SiteRegistrationProps";
import { useButtonAnalytics } from "@mediaspace/hooks";
import { ButtonClickAnalyticsType } from "@mediaspace/shared/types/ButtonClickAnalyticsType";

/* avoid TS errors when accessing window.MktoForms2 */
declare const window: any;

interface Props extends KmsConnectProps {
    userData: UserData;
    sections: RegistrationSectionInfo[];
    recaptchaSiteKey: string;
    recaptchaTheme: "dark" | "light" | undefined;
    recapthcaMode?: "normal" | "invisible" | undefined;
    registrationType: RegistrationType;
    action?: string;
    submitted: boolean;
    onRegister: (redirectUrl?: string) => void;
    fieldsMappingToMarketo?: Record<string, string>[];
    marketoResponseTimeout?: number;
    className?: string;
    validateSsoDomains?: boolean;
    registerOtherUser?: boolean;
}

/**
 * Site registration form container - for registering a new user / editing a users data
 */
const RegistrationFormContainer: FC<Props> = ({
                                                        userData,
                                                        sections,
                                                        recaptchaSiteKey,
                                                        recaptchaTheme,
                                                        recapthcaMode,
                                                        registrationType = RegistrationType.NEW,
                                                        action = "/registration/save",
                                                        onRegister,
                                                        getFromKms,
                                                        fieldsMappingToMarketo = [],
                                                        marketoResponseTimeout = 2000,
                                                        className,
                                                        validateSsoDomains = false,
                                                        registerOtherUser = false
                                                    }) => {

    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [ssoDomainsError, setSsoDomainsError] = useState<boolean>(false);


    const sendButtonAnalytics = useButtonAnalytics();

    // submit the form to kms
    const handleSubmit = (data: Record<string, unknown>) => {
        sendButtonAnalytics("Register", ButtonClickAnalyticsType.REGISTER);

        const url = `${baseUrl}${action}`;
        const marketoResponseTimeoutMil = registrationType === RegistrationType.NEW ? marketoResponseTimeout : 500;

        if (!getFromKms) return;

        setProcessing(true);
        setError(false);
        setErrorMessage("");

        data.registerOtherUser = registerOtherUser;

        getFromKms(
            data as QueryParams,
            (registerResult) => {
                if (registerResult.result === true) {
                    // registration successful
                    sendToMarketo(data);
                    setTimeout(() => {
                        onRegister(registerResult.redirectUrl);
                    }, marketoResponseTimeoutMil);
                } else {
                    setError(true);
                    registerResult.error &&
                    setErrorMessage(registerResult.error);
                }
                setProcessing(false);
            },
            url,
            false
        );
    };

    const sendToMarketo = (data: Record<string, unknown>) => {
        if (registrationType !== RegistrationType.NEW || !window.MktoForms2) {
            return;
        }

        const registrationInfo = data.registrationinfo as Record<string, unknown>;
        const values: Record<string, unknown> = {};

        for (const { registrationField, marketoField } of fieldsMappingToMarketo) {
            if (!isEmpty(registrationInfo[registrationField])) {
                values[marketoField] = registrationInfo[registrationField];
            }
        }

        const marketoForm = window.MktoForms2.allForms()[0];
        marketoForm.addHiddenFields(values);
        // create account on callback event
        marketoForm.onSuccess(() => {
            return false;
        });
        marketoForm.submit();
    };

    // check if email is in ssoDomains
    const isEmailInSsoDomains = (email: string, onReturn: () => void) => {

        if (!getFromKms || !validateSsoDomains || !email) {
            onReturn();
            return;
        }

        setSsoDomainsError(false);

        getFromKms(
            {
                email: email,
            },
            (response) => {
                if (response?.result) {
                    setSsoDomainsError(true);
                }
                onReturn();
                },
            `${baseUrl}/user/check-sso-domains`,
            false
        )
    };

    return (
        <>
            {error && (
                <RegistrationErrors errorMessage={errorMessage} registrationType={registrationType} />
            )}
            <RegistrationForm
                className={className}
                userData={userData}
                sections={sections}
                recaptchaSiteKey={recaptchaSiteKey}
                recaptchaTheme={recaptchaTheme}
                recapthcaMode={recapthcaMode}
                resetCaptcha={error}
                registrationType={registrationType}
                processing={processing}
                onSubmit={handleSubmit}
                isEmailInSsoDomains={isEmailInSsoDomains}
                ssoDomainsError={ssoDomainsError}
            />
        </>
    );
};

export default kmsConnect<Props>(RegistrationFormContainer);
